import React from "react";
import { Input } from "../ui/input";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../ui/dropdown-menu";
import { Search, X, Waypoints, Filter, Frame as FrameIcon, UsersRound, Download } from "lucide-react";
import { stopPointer } from "./treeConstants";

export function TreeSearch({ hidden, value, onChange, open, setOpen, matches, colorFor, onPick }) {
  return (
    <div className={`absolute left-4 top-4 z-30 w-60 ${hidden ? "hidden" : ""}`} onPointerDown={stopPointer}>
      <div className="relative">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
        <Input value={value} onChange={(e) => { onChange(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)} placeholder="Search relatives…" data-testid="tree-search-input" className="h-9 rounded-full border-[#D8E0E8] bg-white/95 pl-9 shadow-sm" />
      </div>
      {open && matches.length > 0 && (
        <div className="mt-1 overflow-hidden rounded-xl border border-[#D8E0E8] bg-white shadow-lg" data-testid="tree-search-results">
          {matches.map((m) => (
            <button key={m.id} onClick={() => onPick(m.id)} data-testid={`search-result-${m.id}`} className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-[#E8EEF3]">
              <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: colorFor(m.id) }} />
              <span className="flex-1 truncate">{m.full_name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const firstName = (m) => m?.full_name?.split(" ")[0];

function pathStatus(pathSel, pathIds, byId, kinship) {
  if (pathIds.length >= 2) {
    const a = firstName(byId[pathSel[0]]), b = firstName(byId[pathSel[1]]);
    const direct = kinship && kinship !== "relative (by marriage)";
    const steps = pathIds.length - 1;
    return (
      <span className="truncate" data-testid="path-kinship">
        <span className="font-semibold">{b}</span>
        {direct ? <> is {a}'s <span className="font-semibold text-[#D4A017]">{kinship}</span></> : <> is a <span className="font-semibold text-[#D4A017]">{kinship || "relative"}</span> of {a}</>}
        <span className="text-[#94A3B8]"> · {steps} step{steps === 1 ? "" : "s"}</span>
      </span>
    );
  }
  if (pathSel.length === 1) return "Now click the second relative";
  if (pathSel.length === 2) return "No connection found between them";
  return "Click two relatives to trace how they're related";
}

export function PathBanner({ pathSel, pathIds, byId, kinship, onClear }) {
  return (
    <div className="absolute left-1/2 top-4 z-30 flex max-w-[70%] -translate-x-1/2 items-center gap-2 rounded-full border border-[#D4A017]/40 bg-white/95 px-3 py-1.5 text-xs shadow-sm" data-testid="path-banner">
      <Waypoints size={13} className="text-[#D4A017]" />
      {pathStatus(pathSel, pathIds, byId, kinship)}
      <button onClick={onClear} data-testid="clear-path" className="ml-1 text-[#64748B] hover:text-[#082D4A]"><X size={13} /></button>
    </div>
  );
}

export function MultiSelectBar({ count, canEdit, groups, palette, onColor, onMoreColors, onResetColor, onAssignGroup, onMakeGroup, onFrame, onExport, onClear }) {
  const Pill = ({ testId, onClick, children }) => <button data-testid={testId} onClick={onClick} className="flex items-center gap-1 rounded-full border border-[#D8E0E8] px-2 py-0.5 text-xs font-medium text-[#082D4A] hover:bg-[#E8EEF3]">{children}</button>;
  const Sep = () => <span className="h-4 w-px bg-[#D8E0E8]" />;
  return (
    <div data-testid="multiselect-bar" onPointerDown={stopPointer} className="absolute left-1/2 top-3 z-[60] flex -translate-x-1/2 flex-wrap items-center justify-center gap-2.5 rounded-2xl border border-[#D8E0E8] bg-white/95 px-3.5 py-2 shadow-lg backdrop-blur">
      <span className="text-xs font-bold text-[#082D4A]">{count} selected</span>
      {canEdit && (<>
        <Sep />
        <div className="flex items-center gap-1.5">
          {palette.map((c) => (<button key={c} data-testid={`multi-swatch-${c}`} onClick={() => onColor(c)} className="h-5 w-5 rounded-full ring-1 ring-black/10 transition-transform hover:scale-110" style={{ backgroundColor: c }} />))}
        </div>
        <Pill testId="multi-more-colors" onClick={onMoreColors}>More…</Pill>
        <button data-testid="multi-color-reset" onClick={onResetColor} className="text-xs font-medium text-[#475569] hover:text-[#082D4A]">Reset</button>
        {groups.length > 0 && (<>
          <Sep />
          <DropdownMenu>
            <DropdownMenuTrigger asChild><button data-testid="multi-group-assign" className="flex items-center gap-1 rounded-full border border-[#D8E0E8] px-2 py-0.5 text-xs font-medium text-[#082D4A] hover:bg-[#E8EEF3]"><UsersRound size={12} /> Add to group</button></DropdownMenuTrigger>
            <DropdownMenuContent align="center">
              {groups.map((g) => (<DropdownMenuItem key={g.id} onClick={() => onAssignGroup(g.id)} data-testid={`multi-group-${g.id}`}><span className="mr-2 h-3 w-3 rounded-full" style={{ backgroundColor: g.color }} />{g.name}</DropdownMenuItem>))}
            </DropdownMenuContent>
          </DropdownMenu>
        </>)}
        <Pill testId="multi-make-group" onClick={onMakeGroup}><UsersRound size={12} /> Make group</Pill>
        <Pill testId="frame-selection-button" onClick={onFrame}><FrameIcon size={12} /> Frame</Pill>
      </>)}
      <Sep />
      <Pill testId="multi-export-selection" onClick={onExport}><Download size={12} /> Export</Pill>
      <Sep />
      <button data-testid="multi-clear" onClick={onClear} className="text-xs font-medium text-[#475569] hover:text-[#082D4A]">Clear</button>
    </div>
  );
}

export function FrameModeHint({ onClose }) {
  return (
    <div className="absolute left-1/2 top-4 z-30 flex -translate-x-1/2 items-center gap-2 rounded-full border border-[#082D4A]/40 bg-white/95 px-3 py-1.5 text-xs shadow-sm" data-testid="frame-mode-hint" onPointerDown={stopPointer}>
      <FrameIcon size={13} className="text-[#082D4A]" /> Drag on the canvas to draw a frame around a family
      <button onClick={onClose} className="ml-1 text-[#64748B] hover:text-[#082D4A]"><X size={13} /></button>
    </div>
  );
}

export function FocusLegend({ groups, onClear }) {
  return (
    <div className="absolute left-1/2 top-4 z-30 flex max-w-[80%] -translate-x-1/2 flex-wrap items-center justify-center gap-x-3 gap-y-1 rounded-2xl border border-[#082D4A]/25 bg-white/95 px-3.5 py-1.5 text-xs shadow-sm" data-testid="focus-legend">
      <span className="flex items-center gap-1.5 font-medium text-[#082D4A]"><Filter size={13} /> Spotlight</span>
      {groups.map((g) => (
        <span key={g.id} className="flex items-center gap-1.5" data-testid={`focus-legend-${g.id}`}>
          <span className="h-3 w-3 rounded-full ring-1 ring-black/10" style={{ backgroundColor: g.color || "#082D4A" }} />
          <span className="font-semibold">{g.name}</span>
          <span className="text-[#94A3B8]">({(g.member_ids || []).length})</span>
        </span>
      ))}
      <button onClick={onClear} data-testid="clear-focus" className="ml-1 text-[#64748B] hover:text-[#082D4A]"><X size={13} /></button>
    </div>
  );
}
